import { useMemo, useState } from "react";
import { Box } from "@mui/joy";
import { Satellite } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import {
    Button,
    ControlledTextField,
    FormRow,
} from "../../../../../../../shared";
import { useNotification } from "../../../../../../../hooks";
import { useProject } from "../../../../../../../hooks/query";
import OrderReportModal from "./OrderReportModal";
import type {
    EagleViewAddress,
    EagleViewPlaceOrderRequest,
    EagleViewPlaceOrderResult,
} from "./types";

export type { EagleViewAddress, EagleViewPlaceOrderRequest, EagleViewPlaceOrderResult };
export { EAGLEVIEW_ORDER_PRODUCTS } from "./products";

type EagleViewOrderManagerProps = {
    projectId: string;
    /** Current order ID (controlled). */
    orderId: string;
    onOrderIdChange: (orderId: string) => void;
    /** True when project already has a linked Order ID (field locked). */
    isLinked: boolean;
    /** Optional status from project (ORDERED / IN_PROGRESS / AVAILABLE / …). */
    status?: string | null;
    /** Address used to prefill the order modal. */
    propertyAddress: EagleViewAddress;
};

function resolveAddress(address: EagleViewAddress): EagleViewAddress {
    return {
        street: address?.street || "",
        city: address?.city || "",
        state: address?.state || "",
        zip: address?.zip || "",
    };
}

function statusDescription(status?: string | null, isLinked?: boolean): string {
    switch (status) {
        case "ORDERED":
            return "Order placed. Measurements will sync when EagleView finishes the report.";
        case "IN_PROGRESS":
            return "EagleView is processing this report. Measurements will sync when ready.";
        case "AVAILABLE":
            return "Report available. Measurements are linked to this project.";
        case "FAILED":
            return "The previous EagleView order failed or was closed. Link an existing Order ID or contact support.";
        default:
            return isLinked
                ? "Order ID linked to this project."
                : "Link an existing report, or order a new one for this property.";
    }
}

/**
 * Overview EagleView Order ID field + “Order report” entry point.
 * Manages linking an existing ID and opening the place-order modal when empty.
 */
export default function EagleViewOrderManager({
    projectId,
    orderId,
    onOrderIdChange,
    isLinked,
    status,
    propertyAddress,
}: EagleViewOrderManagerProps) {
    const { showSuccess, showError } = useNotification();
    const queryClient = useQueryClient();
    const { usePlaceEagleViewOrder, useSyncEagleViewReport } = useProject();
    const placeOrderSvc = usePlaceEagleViewOrder();
    const syncReportSvc = useSyncEagleViewReport();
    const [modalOpen, setModalOpen] = useState(false);

    const canOrder = !isLinked && !orderId.trim();
    const canSync =
        isLinked &&
        (status === "ORDERED" || status === "IN_PROGRESS" || !status);
    const initialAddress = useMemo(
        () => resolveAddress(propertyAddress),
        [propertyAddress],
    );

    const handlePlaceOrder = async (
        request: EagleViewPlaceOrderRequest,
    ): Promise<EagleViewPlaceOrderResult> => {
        const result = await placeOrderSvc.mutateAsync({
            ProjectId: projectId,
            Address: {
                Street: request.address.street,
                City: request.address.city,
                State: request.address.state,
                Zip: request.address.zip,
            },
            ProductId: request.productId,
            DeliveryProductId: request.deliveryProductId,
            MeasurementRequestTypeId: request.measurementRequestTypeId,
            ...(request.claimNumber
                ? { ClaimNumber: request.claimNumber }
                : {}),
            ...(request.poNumber ? { PONumber: request.poNumber } : {}),
        });

        const nextOrderId = String(result?.orderId || result?.reportId || "");
        if (nextOrderId) {
            onOrderIdChange(nextOrderId);
        }

        await queryClient.invalidateQueries({ queryKey: ["project", projectId] });

        showSuccess(
            nextOrderId
                ? `EagleView order ${nextOrderId} placed. Measurements will sync when ready.`
                : "EagleView order placed. Measurements will sync when ready.",
        );

        return { orderId: nextOrderId };
    };

    const handleSync = async () => {
        try {
            const result = await syncReportSvc.mutateAsync({
                ProjectId: projectId,
            });
            await queryClient.invalidateQueries({
                queryKey: ["project", projectId],
            });
            await queryClient.invalidateQueries({
                queryKey: ["measurement", projectId],
            });
            if (result?.measurementsSynced) {
                showSuccess("EagleView measurements synced.");
            } else {
                showSuccess(
                    result?.message ||
                        "Report is not ready yet. Try again shortly.",
                );
            }
        } catch (err: any) {
            showError(err?.message || "Failed to sync EagleView report");
        }
    };

    return (
        <>
            <FormRow
                label="EagleView Order ID"
                description={statusDescription(status, isLinked)}
            >
                <Box
                    sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        width: "100%",
                    }}
                >
                    <ControlledTextField
                        disabled={isLinked}
                        value={orderId}
                        onChange={onOrderIdChange}
                        placeholder="Enter existing Order ID"
                        sx={{ flex: 1 }}
                    />
                    {canOrder && (
                        <Button
                            size="sm"
                            startDecorator={<Satellite size={14} />}
                            onClick={() => setModalOpen(true)}
                            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                        >
                            Order report
                        </Button>
                    )}
                    {canSync && (
                        <Button
                            size="sm"
                            variant="outlined"
                            color="neutral"
                            loading={syncReportSvc.isPending}
                            onClick={handleSync}
                            sx={{ flexShrink: 0, whiteSpace: "nowrap" }}
                        >
                            Sync report
                        </Button>
                    )}
                </Box>
            </FormRow>

            <OrderReportModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                initialAddress={initialAddress}
                onPlaceOrder={handlePlaceOrder}
            />
        </>
    );
}
